export interface FraudRule {
  id: string
  name: string
  description: string
  category: 'pattern' | 'network' | 'behavior' | 'ml'
  enabled: boolean
  weight: number // 0-100
  priority: number // Order of execution
  
  // Configuration
  config: {
    [key: string]: any
  }
  
  // Performance metrics
  metrics: {
    detections: number
    falsePositives: number
    accuracy: number
    lastTriggered: Date
    dailyTriggers: number
  }
  
  // Testing
  testMode: boolean
  testResults?: TestResults
}

export interface TestResults {
  totalTests: number
  accuracyRate: number
  falsePositiveRate: number
  detectionRate: number
}

export const mockRules: FraudRule[] = [
  {
    id: 'seq-phone',
    name: 'Sequential Phone Numbers',
    description: 'Detects sequential phone number patterns indicating automated submissions',
    category: 'pattern',
    enabled: true,
    weight: 85,
    priority: 1,
    config: {
      sequenceThreshold: 3,
      timeWindow: 3600, // 1 hour in seconds
      numberRange: 10, // Check last 10 digits
      allowedGaps: 1 // Allow 1 number gap in sequence
    },
    metrics: {
      detections: 127,
      falsePositives: 3,
      accuracy: 97.6,
      lastTriggered: new Date(Date.now() - 120000), // 2 minutes ago
      dailyTriggers: 42
    },
    testMode: false
  },
  {
    id: 'datacenter-ip',
    name: 'Data Center IP Detection',
    description: 'Identifies traffic from data centers, VPNs, and proxy servers',
    category: 'network',
    enabled: true,
    weight: 75,
    priority: 2,
    config: {
      detectionMethod: 'ipqualityscore',
      allowVpns: false,
      allowProxies: false,
      customRanges: ['169.197.0.0/16', '207.244.0.0/16', '212.102.0.0/16'],
      strictMode: true
    },
    metrics: {
      detections: 89,
      falsePositives: 5,
      accuracy: 94.4,
      lastTriggered: new Date(Date.now() - 300000), // 5 minutes ago
      dailyTriggers: 31
    },
    testMode: false
  },
  {
    id: 'zip-concentration',
    name: 'Zip Code Concentration',
    description: 'Flags high concentration of leads from specific zip codes',
    category: 'pattern',
    enabled: true,
    weight: 65,
    priority: 3,
    config: {
      blacklistedZips: ['80112', '90210', '10001', '33101'],
      concentrationThreshold: 30, // 30% of leads
      timeWindow: 86400, // 24 hours
      minimumLeads: 10 // Need at least 10 leads to trigger
    },
    metrics: {
      detections: 67,
      falsePositives: 8,
      accuracy: 88.1,
      lastTriggered: new Date(Date.now() - 1800000), // 30 minutes ago
      dailyTriggers: 23
    },
    testMode: false
  },
  {
    id: 'suspicious-email',
    name: 'Suspicious Email Domains',
    description: 'Blocks known temporary/disposable email providers and suspicious domains',
    category: 'pattern',
    enabled: true,
    weight: 60,
    priority: 4,
    config: {
      suspiciousDomains: ['armyspy.com', 'dayrep.com', 'rhyta.com', 'teleworm.us', 'cuvox.de'],
      checkDomainAge: true,
      minimumDomainAge: 30, // days
      blockDisposable: true,
      customPatterns: ['*temp*', '*fake*', '*test*']
    },
    metrics: {
      detections: 156,
      falsePositives: 12,
      accuracy: 92.3,
      lastTriggered: new Date(Date.now() - 600000), // 10 minutes ago
      dailyTriggers: 52
    },
    testMode: false
  },
  {
    id: 'velocity-pattern',
    name: 'Velocity Attack Detection',
    description: 'Identifies rapid-fire submissions indicating bot behavior',
    category: 'behavior',
    enabled: true,
    weight: 70,
    priority: 5,
    config: {
      maxSubmissions: 5,
      timeWindow: 120, // 2 minutes
      ipBasedTracking: true,
      deviceBasedTracking: true,
      exponentialBackoff: true
    },
    metrics: {
      detections: 43,
      falsePositives: 2,
      accuracy: 95.3,
      lastTriggered: new Date(Date.now() - 3600000), // 1 hour ago
      dailyTriggers: 15
    },
    testMode: false
  },
  {
    id: 'ml-prediction',
    name: 'Machine Learning Model',
    description: 'Advanced ML model trained on historical fraud patterns',
    category: 'ml',
    enabled: true,
    weight: 55,
    priority: 6,
    config: {
      modelVersion: 'v2.3.1',
      confidenceThreshold: 0.75,
      featureSet: ['behavioral', 'temporal', 'network', 'content'],
      autoRetrain: true,
      retrainFrequency: 'weekly'
    },
    metrics: {
      detections: 234,
      falsePositives: 18,
      accuracy: 92.3,
      lastTriggered: new Date(Date.now() - 180000), // 3 minutes ago
      dailyTriggers: 78
    },
    testMode: false
  },
  {
    id: 'form-behavior',
    name: 'Form Interaction Analysis',
    description: 'Analyzes form filling patterns and mouse movements',
    category: 'behavior',
    enabled: false,
    weight: 45,
    priority: 7,
    config: {
      minFillTime: 15, // seconds
      maxFillTime: 300, // 5 minutes
      checkMouseMovement: true,
      checkTypingSpeed: true,
      checkTabNavigation: true
    },
    metrics: {
      detections: 0,
      falsePositives: 0,
      accuracy: 0,
      lastTriggered: new Date(Date.now() - 86400000), // 1 day ago
      dailyTriggers: 0
    },
    testMode: true
  },
  {
    id: 'device-fingerprint',
    name: 'Device Fingerprinting',
    description: 'Tracks device characteristics and browser fingerprints',
    category: 'network',
    enabled: false,
    weight: 40,
    priority: 8,
    config: {
      trackCanvas: true,
      trackWebGL: true,
      trackFonts: true,
      trackPlugins: true,
      trackTimezone: true,
      allowedDuplicates: 3
    },
    metrics: {
      detections: 0,
      falsePositives: 0,
      accuracy: 0,
      lastTriggered: new Date(Date.now() - 172800000), // 2 days ago
      dailyTriggers: 0
    },
    testMode: true
  }
]

export const sampleTestData = {
  fraudulent: {
    name: "John Doe",
    email: "john.doe@armyspy.com",
    phone: "2262433175",
    zip: "80112",
    ip: "169.197.85.172",
    userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
    timestamp: new Date()
  },
  legitimate: {
    name: "Sarah Johnson",
    email: "sarah.johnson@gmail.com", 
    phone: "5551234567",
    zip: "90210",
    ip: "73.92.12.34",
    userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
    timestamp: new Date()
  },
  edgeCase: {
    name: "Maria Garcia",
    email: "maria@company.co.uk",
    phone: "2262433171", // Sequential but legitimate business
    zip: "80112", // High concentration zip but legitimate
    ip: "203.0.113.1", // Edge case IP
    userAgent: "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36",
    timestamp: new Date()
  }
}